import { RRule, RRuleSet } from 'rrule'
import { Temporal } from 'temporal-polyfill'

/**
 * Parse an RRule string and return a human-readable description
 */
export const rruleToText = (rruleString: string): string => {
  try {
    const rule = RRule.fromString(rruleString)
    return rule.toText()
  } catch {
    return ''
  }
}

/**
 * Create an RRuleSet from event data
 */
export const createRRuleSet = (
  rruleString: string | null | undefined,
  rdateString: string | null | undefined,
  exdateString: string | null | undefined,
  startDate: Date,
  eventTimeZone: string,
  startHour: number,
  startMinute: number,
): RRuleSet => {
  const rruleSet = new RRuleSet()

  if (rruleString) {
    const rule = RRule.fromString(rruleString)
    const rruleOptions = {
      ...rule.origOptions,
      dtstart: startDate,
    }
    const timezoneRule = new RRule(rruleOptions)
    rruleSet.rrule(timezoneRule)
  }

  if (exdateString) {
    exdateString.split(',').forEach((exdate) => {
      const zonedDatetime = Temporal.PlainDateTime.from(exdate)
        .with({
          hour: startHour,
          minute: startMinute,
        })
        .toZonedDateTime(eventTimeZone)
      const jsDate = new Date(zonedDatetime.epochMilliseconds)
      rruleSet.exdate(jsDate)
    })
  }

  if (rdateString) {
    rdateString.split(',').forEach((rdate) => {
      const zonedDatetime = Temporal.PlainDateTime.from(rdate)
        .with({
          hour: startHour,
          minute: startMinute,
        })
        .toZonedDateTime(eventTimeZone)
      const jsDate = new Date(zonedDatetime.epochMilliseconds)
      rruleSet.rdate(jsDate)
    })
  }

  return rruleSet
}

/**
 * Get the next occurrence of a recurring event after a given date
 */
export const getNextOccurrence = (
  rruleSet: RRuleSet,
  afterDate: Date,
): Date | null => {
  return rruleSet.after(afterDate, true)
}

/**
 * Get the first occurrence of a recurring event
 */
export const getFirstOccurrence = (rruleSet: RRuleSet): Date | null => {
  const occurrences = rruleSet.all((date, i) => i < 1)
  return occurrences[0] || null
}

/**
 * Check if an event has irregular recurrence (rdate or exdate)
 */
export const hasIrregularRepeat = (
  rdate: string | null | undefined,
  exdate: string | null | undefined,
): boolean => {
  return !!(rdate || exdate)
}

export { RRule, RRuleSet }
